import { camelCase, trimQuotes } from '@internals/utils'
import type { ast } from 'kubb/kit'
import { parserTs } from '@kubb/parser-ts'
import { File } from 'kubb/jsx'
import type { KubbReactNode } from 'kubb/jsx'
import { ENUM_TYPES_WITH_KEY_SUFFIX, ENUM_TYPES_WITH_RUNTIME_VALUE, ENUM_TYPES_WITH_TYPE_ONLY } from '../constants.ts'
import * as factory from '../factory.ts'
import type { PluginTs, ResolverTs } from '../types.ts'

type EnumOptions = PluginTs['resolvedOptions']['enum']

/**
 * Widens a `namedEnumValues` entry with the optional per-member `description` that the
 * `x-enumDescriptions` / `x-enum-descriptions` vendor extensions provide. The intersection
 * keeps the field readable against published `@kubb/ast` versions that predate it.
 */
type NamedEnumValue = NonNullable<ast.EnumSchemaNode['namedEnumValues']>[number] & { description?: string }

type Props = {
  node: ast.EnumSchemaNode
  enum: EnumOptions
  resolver: ResolverTs
  key?: string | number | null
}

/**
 * Resolves the runtime identifier name and the TypeScript type name for an enum schema node.
 *
 * The raw `node.name` may be a YAML key such as `"enumNames.Type"` which is not a
 * valid TypeScript identifier. The resolver normalizes it. For inline enum properties the adapter
 * already emits a PascalCase+suffix name, so resolution is typically a no-op.
 *
 * When `constCasing` is `'pascalCase'` and `typeSuffix` is empty, the const and the type
 * resolve to the same name, which TypeScript merges into a single value+type declaration.
 */
export function getEnumNames({ node, enum: enumOptions, resolver }: { node: ast.EnumSchemaNode; enum: EnumOptions; resolver: ResolverTs }): {
  enumName: string
  typeName: string
} {
  const resolved = resolver.name(node.name!)
  const enumName = enumOptions.constCasing === 'pascalCase' ? resolved : camelCase(node.name!)
  const typeName = ENUM_TYPES_WITH_KEY_SUFFIX.has(enumOptions.type) ? resolver.enum.keyName(node, enumOptions.typeSuffix) : resolved

  return { enumName, typeName }
}

/**
 * Renders the enum declaration(s) for a single named `EnumSchemaNode`.
 *
 * Depending on `enum.type` this may emit:
 * - A runtime object (`asConst`) plus a `typeof` type alias
 * - A `const enum` or plain `enum` declaration (`constEnum` / `enum`)
 * - A union literal type alias (`literal`)
 *
 * The emitted `File.Source` nodes carry the resolved names so that the barrel
 * index picks up the correct export identifiers.
 */
export function Enum({ node, enum: enumOptions, resolver }: Props): KubbReactNode {
  const { enumName, typeName } = getEnumNames({ node, enum: enumOptions, resolver })

  const [nameNode, typeNode] = factory.createEnumDeclaration({
    name: enumName,
    typeName,
    enums: ((node.namedEnumValues as Array<NamedEnumValue> | undefined)?.map((v) => [trimQuotes(v.name.toString()), v.value, v.description]) ??
      node.enumValues?.filter((v): v is NonNullable<typeof v> => v !== null && v !== undefined).map((v) => [trimQuotes(v.toString()), v]) ??
      []) as Array<[string | number, string | number | boolean, string?]>,
    type: enumOptions.type,
    enumKeyCasing: enumOptions.keyCasing,
  })

  // When the const and the type share a name (pascalCase const + empty typeSuffix) they merge into
  // one declaration. The const carries the barrel export, so keep the type alias in the file but out
  // of the barrel to avoid re-exporting the name a second time as `export type { … }`.
  const namesMerge = !!nameNode && enumName === typeName

  return (
    <>
      {nameNode && (
        <File.Source name={enumName} isExportable isIndexable isTypeOnly={false}>
          {parserTs().print(nameNode)}
        </File.Source>
      )}
      <File.Source
        name={typeName}
        isIndexable={!namesMerge}
        isExportable={!namesMerge && ENUM_TYPES_WITH_RUNTIME_VALUE.has(enumOptions.type)}
        isTypeOnly={ENUM_TYPES_WITH_TYPE_ONLY.has(enumOptions.type)}
      >
        {parserTs().print(typeNode)}
      </File.Source>
    </>
  )
}
